import {useCallback, useEffect} from "react";

import {RenderState, useRenderState} from "@khanacademy/wonder-blocks-core";
import {isTruthy} from "@khanacademy/wonder-stuff-core";
import {useFastlyQuery} from "@khan/data-gql";
import {useRegionOverride} from "@khan/data-region-override";
import {setCoeditorSelectedTemplate} from "@khan/guide-coeditor-core";
import {setPromptVariable} from "@khan/guide-core-helpers";
import {RequestInfo} from "@khan/shared-core";

// eslint-disable-next-line @khan/frontend/require-shared-package-imports-only -- TODO(FEI-6954)
import {REGIONAL_STANDARDS_QUERY} from "~/content-library/queries.ts";
import {useApolloGqlClient} from "~/data-access/gql/use-apollo-gql-client.ts";

import {unloadArticleDetails} from "../save-content-info/load-article-details.ts";
import {unloadExerciseDetails} from "../save-content-info/load-exercise-details.ts";
import {unloadVideoDetails} from "../save-content-info/load-video-details.ts";

type LearnableContentKind =
    | "Article"
    | "Challenge"
    | "Exercise"
    | "Interactive"
    | "Project"
    | "Talkthrough"
    | "Video";

export const CUSTOM_CONTENT_MARKER = "custom";

export function useContentItemPromptVariables(
    contentKind: LearnableContentKind,
    contentId: string,
) {
    const renderState = useRenderState();
    const regionResult = useRegionOverride();
    const {gqlClient} = useApolloGqlClient();

    const contentDescriptor = `${contentKind}:${contentId}`;

    const standardsQueryResult = useFastlyQuery(
        REGIONAL_STANDARDS_QUERY,
        renderState === RenderState.Initial ||
            regionResult.status !== "success" ||
            !contentKind ||
            !contentId
            ? {skip: true}
            : {
                  variables: {
                      contentDescriptor: contentDescriptor,
                      locale: RequestInfo.kaLocale,
                      region: regionResult.regionCode,
                  },
              },
    );

    useEffect(() => {
        if (standardsQueryResult.status !== "success") {
            return;
        }

        const standards = (standardsQueryResult.data?.standards || [])
            .filter(isTruthy)
            .map((s) => `- ${s.setId}: ${s.standardId} (${s.description})`)
            .join("\n");

        setPromptVariable("standards", standards);
    }, [standardsQueryResult]);

    useEffect(() => {
        if (!contentKind || !contentId) {
            return;
        }
    }, [gqlClient, contentKind, contentId]);

    const unsetContentItemPromptVariables = useCallback(() => {
        setCoeditorSelectedTemplate(null);
        setPromptVariable("standards", null);
        unloadExerciseDetails();
        unloadArticleDetails();
        unloadVideoDetails();
    }, []);

    return {unsetContentItemPromptVariables};
}
